import { forwardRef, memo } from 'react';
import type { ButtonProps } from '@mui/material';
import { Button as MuiButton, styled } from '@mui/material';
import type { FormItemUiDefinition } from 'types/formItem';
import { darkerColor } from 'utils/colors';

import { getUIDefinitionIconStyles } from 'utils/styles';
import { getIcon } from 'utils/common';
import { trackClickedButton } from 'utils/posthog/metrics';

interface ExtendedButtonProps extends ButtonProps {
  className?: string;
  formItemUiDefinition?: FormItemUiDefinition;
  phButton?: string;
  phProcess?: string;
  phStep?: string;
}

const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) => prop !== 'uiDefinitionColor' && prop !== 'formItemUiDefinition',
})<ExtendedButtonProps>(({ theme, formItemUiDefinition }) => ({
  minHeight: '42px',
  fontWeight: formItemUiDefinition?.textStyle?.bold === false ? 'normal' : 'bold',
  textDecoration: formItemUiDefinition?.textStyle?.underline ? 'underline' : 'none',
  fontStyle: formItemUiDefinition?.textStyle?.italic ? 'italic' : 'normal',
  ...(formItemUiDefinition?.attributeColor && {
    backgroundColor: formItemUiDefinition.attributeColor,
    '&:hover': {
      backgroundColor: darkerColor(formItemUiDefinition.attributeColor as `#${string}`, 10),
    },
  }),

  '&.MuiButton-outlinedPrimary': {
    color: formItemUiDefinition?.textStyle?.color ?? theme.palette.text.primary,
    borderColor: formItemUiDefinition?.columnBorderColor ?? theme.palette.text.primary,
  },
  '&.Mui-disabled': {
    border: `1px solid ${theme.palette.text.disabled} !important`,
    color: theme.palette.text.disabled,
    '&  > span > .MuiSvgIcon-root': {
      color: `${theme.palette.text.disabled} !important`,
    },
    '&  > .MuiSvgIcon-root': {
      color: `${theme.palette.text.disabled} !important`,
    },
  },
  '&.MuiButton-tinted.Mui-disabled': {
    border: 'none !important',
  },
  '&.MuiButton-containedPrimary': {
    border: `1px solid ${theme.palette.primary.main}`,
    '&:hover': {
      border: `1px solid ${theme.palette.text.primary}`,
    },
    '& > span > .MuiSvgIcon-root': {
      color: theme.palette.common.white,
    },
  },
}));

const createStartIcon = (formItemUiDefinition?: FormItemUiDefinition | null) => {
  if (formItemUiDefinition && formItemUiDefinition.icon) {
    const Icon = getIcon(formItemUiDefinition.icon);
    return <Icon sx={getUIDefinitionIconStyles(formItemUiDefinition)} />;
  }
  return null;
};

export const Button = memo(
  forwardRef<HTMLButtonElement, ExtendedButtonProps>(({ phButton, phProcess, phStep, onClick, ...rest }, ref) => {
    return (
      <StyledButton
        {...rest}
        ref={ref}
        startIcon={rest.startIcon !== undefined ? rest.startIcon : createStartIcon(rest.formItemUiDefinition)}
        onClick={(e) => {
          if (phButton) {
            try {
              trackClickedButton({ button: phButton, process: phProcess, step: phStep });
            } catch {
              /* noop */
            }
          }
          onClick?.(e as unknown as React.MouseEvent<HTMLButtonElement, MouseEvent>);
        }}
      />
    );
  })
);
